/**
 * FeatureTourProvider — engine + page-awareness for spotlight feature tours.
 *
 * Mirrors the FutureBotProvider pattern: lives INSIDE the Router (needs
 * useLocation) and above all routed pages so:
 *   1. It matches the current route against the static tourRegistry and
 *      auto-starts unseen tours on first visit (per-user, Firestore-backed).
 *   2. Any page can register named actions with useTourActions() that steps
 *      invoke via `action: '<name>'` (e.g. open a modal before highlighting
 *      fields inside it).
 *
 * The visual layer is <TourOverlay /> (rendered as a sibling of the routes);
 * the navbar <TourHelpButton /> replays the current page's tours on demand.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { canAccessNewFeatures } from '../../utils/featureAccess';
import {
  TOURS,
  toursForPath,
  menuToursForPath,
  getTourById,
  audienceAllows,
  isTourAdmin,
} from './tourRegistry';
import { useSeenTours } from './useSeenTours';
import { stepSelector, waitForElement, scrollToTarget, diagnoseTarget } from './targetUtils';

const AUTO_START_DELAY_MS = 800;
const DEFAULT_STEP_WAIT_MS = 5000;
const ACTION_RETRY_WAIT_MS = 1500;
const NAV_TOUR_START_DELAY_MS = 400; // let the destination page mount before starting
const NAV_TOUR_MAX_AGE_MS = 15000; // drop a queued cross-route tour if navigation never lands

const FeatureTourContext = createContext(null);

export function FeatureTourProvider({ children }) {
  const location = useLocation();
  const history = useHistory();
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const { isImpersonating } = useImpersonation();
  const { seenTours, loading: seenLoading, markTourSeen, resetTour } = useSeenTours();

  // active = { tour, source: 'auto' | 'manual' } | null
  const [active, setActive] = useState(null);
  const [stepIndex, setStepIndex] = useState(-1);
  const [targetEl, setTargetEl] = useState(null);

  // Refs kept eagerly in sync so async step transitions and event handlers
  // always read fresh values without re-subscribing.
  const activeRef = useRef(null);
  const stepIndexRef = useRef(-1);
  const runIdRef = useRef(0); // bumping cancels any in-flight step transition
  const transitioningRef = useRef(false); // Next/Back re-entrancy guard
  const actionsSetRef = useRef(new Set()); // Set<actionsObject>, last registered wins
  const attemptedAutoIdsRef = useRef(new Set()); // session guard against re-triggering
  // Cross-route menu launch in flight: { tourId, queuedAt } — started once the
  // route matches the tour (see the location effect below).
  const pendingNavTourRef = useRef(null);

  const clearTourState = useCallback(() => {
    runIdRef.current += 1;
    transitioningRef.current = false;
    activeRef.current = null;
    stepIndexRef.current = -1;
    setActive(null);
    setStepIndex(-1);
    setTargetEl(null);
  }, []);

  /** End the tour and persist it as seen ('completed' | 'dismissed'). */
  const endTour = useCallback(
    (status = 'dismissed') => {
      const current = activeRef.current;
      clearTourState();
      if (current && status) {
        markTourSeen(current.tour.id, current.tour.version || 1, status);
      }
    },
    [clearTourState, markTourSeen]
  );

  /** End the tour WITHOUT marking it seen (aborts retry on the next visit). */
  const abortTour = useCallback(() => {
    clearTourState();
  }, [clearTourState]);

  /** Last-registered action wins (page registers first, modals may override). */
  const resolveAction = useCallback((name) => {
    let found = null;
    actionsSetRef.current.forEach((obj) => {
      if (obj && typeof obj[name] === 'function') found = obj[name];
    });
    return found;
  }, []);

  /**
   * Advance to step `index` of `tour`: run the step action, wait for the
   * target to exist & be visible, scroll/settle, then activate it. Steps with
   * missing targets are skipped forward; walking past the last step completes
   * the tour. Any newer transition/end invalidates this one via runIdRef.
   */
  const goToStep = useCallback(
    async (tour, index, source, { initial = false } = {}) => {
      const runId = ++runIdRef.current;
      const isStale = () => runIdRef.current !== runId;
      const steps = tour.steps || [];
      const debug = process.env.NODE_ENV === 'development';
      transitioningRef.current = true;

      try {
        for (let i = index; i < steps.length; i += 1) {
          const step = steps[i];
          if (debug) {
            console.info(
              `FeatureTour: "${tour.id}" → step ${i + 1}/${steps.length}` +
                ` (target: ${step.target || step.selector}${step.action ? `, action: ${step.action}` : ''})`
            );
          }

          if (step.action) {
            const fn = resolveAction(step.action);
            if (fn) {
              try {
                await fn();
              } catch (error) {
                console.warn(`FeatureTour: action "${step.action}" threw`, error);
              }
            } else {
              console.warn(
                `FeatureTour: no action "${step.action}" registered for tour "${tour.id}" — did the page call useTourActions()? (A stale hot-reload can drop registrations — hard-refresh the tab.)`
              );
            }
            if (isStale()) return;
          }

          let el = null;
          try {
            el = await waitForElement(stepSelector(step), {
              timeoutMs: step.waitForMs || DEFAULT_STEP_WAIT_MS,
            });
          } catch (error) {
            if (isStale()) return;

            // Step actions are idempotent state setters ("switch to the Tasks
            // tab"), so if the target never appeared, re-run the action once
            // and re-wait briefly — self-heals a lost/raced state update.
            if (step.action) {
              const fn = resolveAction(step.action);
              if (fn) {
                if (debug) {
                  console.info(`FeatureTour: target missing — retrying action "${step.action}" for step ${i + 1}`);
                }
                try {
                  await fn();
                } catch (_) {
                  // first invocation already warned
                }
                if (isStale()) return;
                el = await waitForElement(stepSelector(step), {
                  timeoutMs: ACTION_RETRY_WAIT_MS,
                }).catch(() => null);
                if (isStale()) return;
              }
            }

            if (!el) {
              if (debug) diagnoseTarget(stepSelector(step));
              if (initial && i === 0 && source === 'auto') {
                // First target missing when an auto tour cold-starts (likely
                // role-gated or the page changed): bail silently, unmarked, so it
                // can retry on a later visit. Doesn't apply to Back/Next moves.
                abortTour();
                return;
              }
              console.warn(`FeatureTour: skipping step ${i + 1} of "${tour.id}" — ${error.message}`);
              continue;
            }
          }
          if (isStale()) return;

          await scrollToTarget(el);
          if (isStale()) return;

          stepIndexRef.current = i;
          setStepIndex(i);
          setTargetEl(el);
          return;
        }

        // Walked past the end (Next on the last step, or every remaining step skipped).
        if (!isStale()) endTour('completed');
      } finally {
        // Only the newest transition owns the flag; a stale run must not
        // clear it while a newer one is still in flight.
        if (!isStale()) transitioningRef.current = false;
      }
    },
    [resolveAction, abortTour, endTour]
  );

  const startTour = useCallback(
    (tourId, { source = 'manual' } = {}) => {
      const tour = getTourById(tourId);
      if (!tour) {
        console.warn(`FeatureTour: unknown tour "${tourId}"`);
        return;
      }
      const session = { tour, source };
      activeRef.current = session;
      stepIndexRef.current = -1;
      setActive(session);
      setStepIndex(-1);
      setTargetEl(null);
      goToStep(tour, 0, source, { initial: true });
    },
    [goToStep]
  );

  /**
   * Start a tour from a navbar menu entry (see menuToursForPath). In-place
   * tours start immediately; cross-route entries either run the page action
   * (which navigates — e.g. open the user's newsletter) or push the tour's
   * own route, then the location effect starts the tour once the path matches.
   */
  const startTourFromMenu = useCallback(
    (entry) => {
      if (!entry || !entry.tour) return;
      const launch = entry.launch || { type: 'inplace' };
      if (launch.type === 'inplace') {
        startTour(entry.tour.id, { source: 'manual' });
        return;
      }
      pendingNavTourRef.current = { tourId: entry.tour.id, queuedAt: Date.now() };
      if (launch.type === 'action') {
        const fn = resolveAction(launch.action);
        if (!fn) {
          pendingNavTourRef.current = null;
          console.warn(
            `FeatureTour: no action "${launch.action}" registered for menu launch of "${entry.tour.id}" — did the page call useTourActions()?`
          );
          return;
        }
        Promise.resolve()
          .then(fn)
          .catch((error) => {
            pendingNavTourRef.current = null;
            console.warn(`FeatureTour: menu launch action "${launch.action}" threw`, error);
          });
        return;
      }
      if (launch.to) history.push(launch.to);
      else pendingNavTourRef.current = null;
    },
    [startTour, resolveAction, history]
  );

  const next = useCallback(() => {
    const current = activeRef.current;
    if (!current) return;
    // Ignore clicks while a transition is in flight: re-entering would bump
    // runId and RESTART the same transition (incl. its waitForMs timer), so
    // an impatient user hammering "Next" could freeze the tour on one step
    // forever. Transitions are time-bounded, so the guard always clears.
    if (transitioningRef.current) return;
    const nextIndex = stepIndexRef.current + 1;
    if (nextIndex >= (current.tour.steps || []).length) {
      endTour('completed');
      return;
    }
    goToStep(current.tour, nextIndex, current.source);
  }, [endTour, goToStep]);

  const back = useCallback(() => {
    const current = activeRef.current;
    if (!current) return;
    if (transitioningRef.current) return;
    const prevIndex = stepIndexRef.current - 1;
    if (prevIndex < 0) return;
    goToStep(current.tour, prevIndex, current.source);
  }, [goToStep]);

  /**
   * Register named step actions for the mounted page. Returns an unregister
   * function; identity-based so a newer registration isn't wiped by an older
   * unmount (same semantics as FutureBot's registerAgentPage).
   */
  const registerTourActions = useCallback((actions) => {
    if (!actions) return () => {};
    actionsSetRef.current.add(actions);
    return () => {
      actionsSetRef.current.delete(actions);
    };
  }, []);

  /** Is the current user eligible for this tour (audience + new-features gate)? */
  const isEligible = useCallback(
    (tour) => {
      if (!tour) return false;
      const isAdmin = isTourAdmin({ currentUser, SuperAdmin, Roles });
      if (!audienceAllows(tour.audience, isAdmin)) return false;
      if (tour.requiresNewFeatures && !canAccessNewFeatures({ currentUser, SuperAdmin, Roles })) {
        return false;
      }
      return true;
    },
    [currentUser, SuperAdmin, Roles]
  );

  // Tours on this page the CURRENT USER is eligible for. Drives both
  // auto-start and the navbar tutorials menu. startTour() / window.__tour
  // deliberately bypass eligibility (dev override).
  const toursForCurrentPage = useMemo(
    () => toursForPath(location.pathname).filter(isEligible),
    [location.pathname, isEligible]
  );

  // Menu entries for the navbar dropdown: in-place tours PLUS tours listed
  // here via menuAlso (cross-route), each { tour, section, launch }. Same
  // eligibility gating; auto-start still uses toursForCurrentPage only.
  const menuToursForCurrentPage = useMemo(
    () => menuToursForPath(location.pathname).filter((entry) => isEligible(entry.tour)),
    [location.pathname, isEligible]
  );

  /**
   * Auto-start a tour for a NON-ROUTE surface (drawer/modal) when it opens.
   * Same guards as route auto-start: seen-state, eligibility, impersonation,
   * session attempt guard, and never interrupts an active tour.
   * Returns true if the tour actually started. Prefer the useAutoTour() hook.
   */
  const requestAutoTour = useCallback(
    (tourId) => {
      if (seenLoading || !currentUser || isImpersonating) return false;
      if (activeRef.current) return false;
      const tour = getTourById(tourId);
      if (!tour) {
        console.warn(`FeatureTour: unknown tour "${tourId}"`);
        return false;
      }
      if (!isEligible(tour)) return false;
      if (attemptedAutoIdsRef.current.has(tourId)) return false;
      const seen = seenTours[tourId];
      if (seen && (seen.version || 0) >= (tour.version || 1)) return false;
      attemptedAutoIdsRef.current.add(tourId);
      startTour(tourId, { source: 'auto' });
      return true;
    },
    [seenLoading, currentUser, isImpersonating, isEligible, seenTours, startTour]
  );

  // Route change mid-tour: end silently (not marked seen — re-offers next visit).
  // MUST key only on pathname + the stable abortTour: extra callback deps can
  // change identity on unrelated re-renders and abort a healthy tour.
  useEffect(() => {
    if (activeRef.current) abortTour();
  }, [location.pathname, abortTour]);

  // Landing half of cross-route menu launches: once the queued tour's route
  // matches, start it after a short mount delay. The ref is cleared when the
  // timer FIRES (not before), so a re-render that re-runs this effect merely
  // re-arms the timer instead of dropping the launch.
  useEffect(() => {
    const pending = pendingNavTourRef.current;
    if (!pending) return undefined;
    if (Date.now() - pending.queuedAt > NAV_TOUR_MAX_AGE_MS) {
      pendingNavTourRef.current = null; // navigation never landed (guarded route, failed action)
      return undefined;
    }
    const tour = getTourById(pending.tourId);
    const lands =
      !!tour &&
      (tour.routes || []).some((route) => matchPath(location.pathname, { path: route, exact: true }));
    // Not there yet (async launch actions navigate late) — keep waiting.
    if (!lands) return undefined;
    const timer = setTimeout(() => {
      pendingNavTourRef.current = null;
      if (!activeRef.current) startTour(pending.tourId, { source: 'manual' });
    }, NAV_TOUR_START_DELAY_MS);
    return () => clearTimeout(timer);
  }, [location.pathname, startTour]);

  // Auto-start: first unseen (or version-bumped) eligible autoStart tour for
  // this page (eligibility already applied by toursForCurrentPage).
  useEffect(() => {
    if (seenLoading || !currentUser || isImpersonating) return undefined;
    if (activeRef.current) return undefined;

    const candidate = toursForCurrentPage.find((tour) => {
      if (!tour.autoStart) return false;
      if (attemptedAutoIdsRef.current.has(tour.id)) return false;
      const seen = seenTours[tour.id];
      if (seen && (seen.version || 0) >= (tour.version || 1)) return false;
      return true;
    });
    if (!candidate) return undefined;

    const timer = setTimeout(() => {
      if (activeRef.current) return;
      attemptedAutoIdsRef.current.add(candidate.id);
      startTour(candidate.id, { source: 'auto' });
    }, AUTO_START_DELAY_MS);
    return () => clearTimeout(timer);
  }, [toursForCurrentPage, seenLoading, seenTours, currentUser, isImpersonating, startTour]);

  // Dev helpers for testing with `npm start`:
  //   window.__tour.list() / .eligible() / .start(id) / .reset(id) / .seen()
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return undefined;
    window.__tour = {
      list: () => TOURS.map((t) => t.id),
      eligible: () => toursForCurrentPage.map((t) => t.id),
      start: (id) => startTour(id, { source: 'manual' }),
      reset: (id) => {
        attemptedAutoIdsRef.current.delete(id);
        return resetTour(id);
      },
      seen: () => seenTours,
    };
    return () => {
      delete window.__tour;
    };
  }, [startTour, resetTour, seenTours, toursForCurrentPage]);

  const value = useMemo(
    () => ({
      activeTour: active ? active.tour : null,
      tourSource: active ? active.source : null,
      stepIndex,
      targetEl,
      startTour,
      startTourFromMenu,
      endTour,
      next,
      back,
      registerTourActions,
      requestAutoTour,
      toursForCurrentPage,
      menuToursForCurrentPage,
      seenTours,
    }),
    [
      active,
      stepIndex,
      targetEl,
      startTour,
      startTourFromMenu,
      endTour,
      next,
      back,
      registerTourActions,
      requestAutoTour,
      toursForCurrentPage,
      menuToursForCurrentPage,
      seenTours,
    ]
  );

  return <FeatureTourContext.Provider value={value}>{children}</FeatureTourContext.Provider>;
}

export function useFeatureTour() {
  const ctx = useContext(FeatureTourContext);
  if (!ctx) {
    throw new Error('useFeatureTour must be used within a FeatureTourProvider');
  }
  return ctx;
}

/**
 * Page-side hook: register named tour actions while mounted.
 * Pass a MEMOIZED object (or null to skip):
 *
 *   useTourActions(useMemo(() => ({ openAddContact: onAddContactOpen }), [onAddContactOpen]));
 *
 * Steps reference these by name: { action: 'openAddContact', ... }
 */
export function useTourActions(actions) {
  const { registerTourActions } = useFeatureTour();
  useEffect(() => {
    if (!actions) return undefined;
    return registerTourActions(actions);
  }, [registerTourActions, actions]);
}

/**
 * Surface-side hook for NON-ROUTE surfaces (drawers, modals): auto-start a
 * tour the first time the surface becomes visible. Route-scoped tours don't
 * need this — the provider auto-starts those on navigation. The registry
 * entry should set autoStart: false so the route effect never races this.
 *
 *   useAutoTour(isOpen && !!contact, 'contact-drawer');
 *
 * All the usual guards apply (seen-state, audience, impersonation, one
 * attempt per session, never interrupts an active tour).
 */
export function useAutoTour(shouldRun, tourId, { delayMs = 600 } = {}) {
  const { requestAutoTour } = useFeatureTour();
  useEffect(() => {
    if (!shouldRun || !tourId) return undefined;
    const timer = setTimeout(() => {
      requestAutoTour(tourId);
    }, delayMs);
    return () => clearTimeout(timer);
  }, [shouldRun, tourId, delayMs, requestAutoTour]);
}

export default FeatureTourContext;
